/* eslint-disable react/no-this-in-sfc */
import CKEditor from '@ckeditor/ckeditor5-react';
import { PwcCKEditors } from '@pwc/pwc-deals-rich-text-editor';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { Button, Icon } from '@pwc/pwc-deals-ui';
import { DateTime } from 'luxon';
import _isDate from 'lodash/isDate';
import classnames from 'classnames';
import { structuredAnalysisTypes } from 'common/constants/StructuredAnalysisTypes';
import {
  getCommentsHasLoaded,
  getCurrentComments,
  getCurrentCommentThreads,
  loadCommentsThunk,
  getCommentPanelRendered,
  setHasCommentSelection,
  getHasCommentSelection,
  getCommentPanelOpened,
  setCommentPanelOpened,
  setIsLoading,
  getCommentsIsLoading,
  setOpenNewCommentThread,
  getOpenNewCommentThread,
  getActiveThreadId,
  setActiveThreadId,
  getViewingStatus,
  getResolvedThreadId,
  setResolvedThreadId,
} from '../../features/comments/commentsSlice';
import {
  getProject,
  getUsersForCommenting,
  loadUsersForCommenting,
  getUsersForCommentingLoaded,
} from '../../features/project/projectSlice';
import { getCommentsAdapter } from './CommentsAdapter';
import CustomCommentThreadView from './views/CustomCommentThreadView';
import CustomCommentView from './views/CustomCommentView';
import { useCurrentUser } from '../hooks/useCurrentUser';
import styles from './CommentEditor.module.scss';
import featureFlags, {
  isInlineCommentingEnabled,
} from '../helpers/featureFlags';
import {
  scrollToLastComment,
  getFeedItems,
  customItemRenderer,
} from './Helpers.ts';
import { COMMENT_VIEWING_STATUS } from '../../features/comments/constants';
import { getFakeClientUser } from '../redux/security/loggedInUser/loggedInUserSlice';

const { ClassicEditor } = PwcCKEditors;

// eslint-disable-next-line import/no-mutable-exports
export let currentThread = {};

const MentionPlugin = ClassicEditor.builtinPlugins.find(
  (plugin) => plugin.pluginName === 'Mention'
);

const { CustomMentionView } = ClassicEditor;

export const filterExistingComments = (commentThreads, commentRepository) =>
  commentThreads.filter(
    (thread) => !(thread.id && commentRepository.hasCommentThread(thread.id))
  );

export const formatThread = (thread) => {
  const comments = (thread.comments || []).map((comment) => ({
    ...comment,
    authorId: (comment.authorId || '').toString(),
  }));

  return {
    ...thread,
    threadId: thread.id,
    comments,
  };
};

const CommentEditor = ({
  children,
  entityType,
  entityId,
  entityVersion,
  isEntitySharedWithClient,
  isProjectDisabled,
  onSelectionChange,
  formattingPlugin,
  loadComments = false,
}) => {
  const dispatch = useDispatch();
  const { projectId } = useParams();
  const [commentEditor, setCommentEditor] = useState();
  const [channelId] = useState(uuidv4());
  const [lastTextChange, setLastTextChange] = useState();

  const project = useSelector(getProject);
  const hasLoadedComments = useSelector(getCommentsHasLoaded);
  const commentThreads = useSelector(getCurrentCommentThreads);
  const comments = useSelector(getCurrentComments);
  const sidebarHasRendered = useSelector(getCommentPanelRendered);
  const sidebarIsVisible = useSelector(getCommentPanelOpened);
  const hasCommentSelection = useSelector(getHasCommentSelection);
  const usersForCommenting = useSelector(getUsersForCommenting);
  const usersForCommentingLoaded = useSelector(getUsersForCommentingLoaded);
  const activeThreadId = useSelector(getActiveThreadId);
  const openNewCommentThread = useSelector(getOpenNewCommentThread);
  const isLoading = useSelector(getCommentsIsLoading);
  const viewingStatus = useSelector(getViewingStatus);
  const resolvedThreadId = useSelector(getResolvedThreadId);
  const isFakeClient = useSelector(getFakeClientUser);

  const currentUser = useCurrentUser(project);

  const location = useLocation();
  const isStructureAnalysis = useCallback(() => {
    return Object.values(structuredAnalysisTypes).some((x) => {
      return location.pathname.indexOf(x) > -1;
    });
  }, [location.pathname]);

  useEffect(() => {
    dispatch(loadUsersForCommenting(projectId));
  }, [dispatch, projectId]);

  useEffect(() => {
    dispatch(setActiveThreadId(''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEntitySharedWithClient]);

  useEffect(() => {
    if (!loadComments) {
      return;
    }
    if (!isInlineCommentingEnabled || isStructureAnalysis()) {
      dispatch(
        loadCommentsThunk(
          projectId,
          entityType,
          entityId,
          entityVersion,
          isEntitySharedWithClient
        )
      );
    }
  }, [
    dispatch,
    projectId,
    entityType,
    entityId,
    entityVersion,
    sidebarIsVisible,
    isEntitySharedWithClient,
    loadComments,
    isStructureAnalysis,
  ]);

  useEffect(() => {
    const checkSelection = () => {
      const ckEditor = document.querySelector(`#ckEditor-${channelId}`);

      if (ckEditor) {
        const activeThread = document.querySelector('.ck-thread--active');
        const selection = document.getSelection();

        const selectionInActiveThread =
          activeThread &&
          activeThread.contains(selection.anchorNode) &&
          activeThread.contains(selection.focusNode);
        if (selectionInActiveThread) {
          return;
        }

        const selectionLength = selection.toString().length;
        const selectionInEditor =
          ckEditor.contains(selection.anchorNode) &&
          ckEditor.contains(selection.focusNode);

        if (selectionLength && selectionInEditor) {
          dispatch(setHasCommentSelection(true));
        } else {
          dispatch(setHasCommentSelection(false));
        }

        const addSelectionCommentButton = document.querySelector(
          `#addSelectionCommentButton-${channelId}`
        );
        if (addSelectionCommentButton) {
          addSelectionCommentButton.style.top = `${
            selection.getRangeAt(0).getBoundingClientRect().top
          }px`;
        }
      }
    };

    if (featureFlags.selectionCommentsEnabled) {
      document.addEventListener('selectionchange', checkSelection);
    }

    return () => {
      if (featureFlags.selectionCommentsEnabled) {
        document.removeEventListener('selectionchange', checkSelection);
      }
    };
  }, [dispatch, channelId]);

  const CommentsAdapter = getCommentsAdapter(
    projectId,
    currentUser,
    comments,
    entityType,
    entityId,
    entityVersion,
    commentThreads,
    dispatch,
    formatThread,
    usersForCommenting,
    isEntitySharedWithClient
  );

  // We have to do this because CKEditor will try to
  // use the sidebar *before* it's been rendered :(
  const [sidebar, setSidebar] = useState();
  useEffect(() => {
    if (sidebarHasRendered && loadComments) {
      setSidebar(document.querySelector('#comment-panel'));
    }
  }, [sidebarHasRendered, loadComments]);

  useEffect(() => {
    if (!commentEditor) {
      return () => {};
    }

    const selectLastItem = document.querySelector(
      '.ck-sidebar-item:last-child'
    );
    const selectTopItem = document.querySelector('.top');

    const newThreadButtonSelectors = ['.new-comment-button'];
    const commentsRepository = commentEditor.plugins.get('CommentsRepository');

    const cancelButtonsWithListeners = [];
    const closeNewCommentThread = () =>
      dispatch(setOpenNewCommentThread(false));
    const openNewThread = () => {
      commentsRepository.openNewCommentThread({
        channelId,
        threadId: uuidv4(),
        target: selectLastItem || selectTopItem,
      });

      const cancelButtons = document.querySelectorAll(
        '.ck-comment__input-actions--cancel'
      );

      cancelButtons.forEach((cancelButton) => {
        if (cancelButton) {
          cancelButton.addEventListener('click', closeNewCommentThread);
          cancelButtonsWithListeners.push(cancelButton);
        }
      });

      dispatch(setOpenNewCommentThread(true));
      scrollToLastComment();
    };

    const buttonsWithListeners = [];
    newThreadButtonSelectors.forEach((selector) => {
      const button = document.querySelector(selector);

      if (button) {
        button.addEventListener('click', openNewThread);
        buttonsWithListeners.push(button);
      }
    });

    return () => {
      buttonsWithListeners.forEach((button) => {
        button.removeEventListener('click', openNewThread);
      });
      cancelButtonsWithListeners.forEach((cancelButton) => {
        cancelButton.removeEventListener('click', openNewThread);
      });
      if (commentsRepository) {
        commentsRepository.getCommentThreads().map((c) => c.remove());
      }
    };
  }, [commentEditor, channelId, isProjectDisabled, dispatch]);

  useEffect(() => {
    if (!isLoading && openNewCommentThread && activeThreadId) {
      scrollToLastComment();
    }
  }, [isLoading, openNewCommentThread, activeThreadId]);

  useEffect(() => {
    if (commentEditor && loadComments) {
      const commentRepository = commentEditor.plugins.get('CommentsRepository');

      commentThreads
        .filter(
          (thread) =>
            !(thread.id && commentRepository.hasCommentThread(thread.id))
        )
        .forEach((thread) => {
          const formattedThread = formatThread(thread);
          commentRepository.addCommentThread({
            ...formattedThread,
            target: document.querySelector('.bottom'),
          });
        });

      // we just resolved comment and showing only unresolved comments
      // focus is going to be put on the previous comment to fix the necessity of scrolling up or down
      if (
        resolvedThreadId &&
        viewingStatus === COMMENT_VIEWING_STATUS.UNRESOLVED
      ) {
        const activeThreadIndex =
          commentThreads.findIndex((thread) => thread.id === resolvedThreadId) -
          1;

        if (activeThreadIndex > 0) {
          let previousThreadId = null;
          for (let index = activeThreadIndex; index >= 0; index -= 1) {
            const prevThread = commentThreads?.[index];
            if (prevThread && prevThread.resolved === false) {
              previousThreadId = prevThread.id;
              break;
            }
          }

          if (previousThreadId) {
            dispatch(setActiveThreadId(previousThreadId));
          }
        }

        dispatch(setResolvedThreadId(''));
      }
      if (
        activeThreadId &&
        commentRepository.hasCommentThread(activeThreadId)
      ) {
        try {
          commentRepository.setActiveCommentThread(activeThreadId);
        } catch (e) {
          // This will catch the scenario when attempting to activate
          // a comment that has been detattched in CKE repository
          commentRepository.setActiveCommentThread();
        }
      }

      // dispatch at the end of the event loop call stack so CKEditor finishes rendering comments
      setTimeout(() => {
        dispatch(setIsLoading(false));
        const activeThread = document.querySelector('.ck-thread--active');
        // return focus on the active thread after the comments are re-fetched from API (e.g.: after resolve comment)
        // needs to run after CKEditor finishes rendering
        const currentlyFocusedElement = document.activeElement;
        const focusOnCommentInput =
          currentlyFocusedElement.classList.contains('ck-content');

        if (
          activeThread !== null &&
          !focusOnCommentInput &&
          currentlyFocusedElement !== activeThread
        ) {
          activeThread.focus();
        }
      }, 0);

      if (isProjectDisabled) {
        commentRepository.switchReadOnly(true);
      }

      const handleActiveThreadChange = (evt, propName, activeThread) => {
        if (activeThread) {
          const activeSavedThread = commentThreads.find(
            (thread) => thread.id === activeThread.id
          );

          currentThread = activeSavedThread;

          dispatch(setActiveThreadId(activeThread.id));
        } else {
          const activeInputContainer = document.querySelector(
            '.ck-comment__input-container.ck-comment__input-container--active'
          );

          if (activeInputContainer) {
            const input = activeInputContainer.querySelector(
              '.ck.ck-content.ck-editor__editable > p'
            );

            if (!input || input.innerText.trim().length === 0) {
              dispatch(setOpenNewCommentThread(false));
            }
          }
        }
      };

      commentRepository.on(
        'change:activeCommentThread',
        handleActiveThreadChange
      );

      return () => {
        commentRepository.off(
          'change:activeCommentThread',
          handleActiveThreadChange
        );
      };
    }
    return () => {};
  }, [
    commentEditor,
    loadComments,
    commentThreads,
    isProjectDisabled,
    dispatch,
    activeThreadId,
    resolvedThreadId,
    viewingStatus,
  ]);

  useEffect(() => {
    const handleCommentMarkerClick = () => {
      if (featureFlags.selectionCommentsEnabled) {
        dispatch(setCommentPanelOpened(true));
      }
    };

    const commentMarkers = document.querySelectorAll('.ck-comment-marker');
    commentMarkers.forEach((marker) => {
      marker.addEventListener('click', handleCommentMarkerClick);
    });

    return () => {
      commentMarkers.forEach((marker) => {
        marker.removeEventListener('click', handleCommentMarkerClick);
      });
    };
  }, [commentEditor, dispatch]);

  const display = children ? 'flex' : 'none';

  const extraPlugins = [CommentsAdapter];
  if (formattingPlugin) {
    extraPlugins.push(formattingPlugin);
  }

  return (
    <div
      id={`ckEditor-${channelId}`}
      style={{ display, position: 'relative', width: '100%' }}
      data-testid="comment-editor"
      className={classnames(styles['comment-editor'], {
        'selection-comments-disabled': !featureFlags.selectionCommentsEnabled,
      })}
    >
      {hasCommentSelection && !isProjectDisabled && (
        <Button
          className={`${styles['add-selection-comment-button']} ${
            sidebarIsVisible ? styles['open-sidebar'] : ''
          }`}
          aria-label="add comment for selection"
          data-testid="add-selection-comment-button"
          id={`addSelectionCommentButton-${channelId}`}
          onClick={() => {
            if (commentEditor) {
              dispatch(setCommentPanelOpened(true));
              commentEditor.execute('addCommentThread', {
                threadId: uuidv4(),
              });
            }
          }}
        >
          <Icon name="comment-plus" className="fill icon-orange" width={25} />
        </Button>
      )}

      {(hasLoadedComments || !loadComments) &&
        project.id &&
        currentUser &&
        sidebar &&
        usersForCommentingLoaded && (
          <CKEditor
            onInit={(editor) => {
              const annotations = editor.plugins.get('AnnotationsUIs');
              annotations.switchTo('wideSidebar');
              editor.editing.view.change((writer) => {
                writer.setAttribute(
                  'spellcheck',
                  'false',
                  editor.editing.view.document.getRoot()
                );
              });
              setCommentEditor(editor);
            }}
            onChange={(_, editor) => {
              const text = editor.getData();
              if (text !== lastTextChange) {
                onSelectionChange(text);
                setLastTextChange(text);
              }
            }}
            editor={ClassicEditor}
            config={{
              licenseKey: process.env.REACT_APP_CK_EDITOR_LICENSE,
              extraPlugins,
              toolbar: {
                items: [],
              },
              sidebar: {
                container: sidebar,
              },
              locale: {
                dateTimeFormat: (date) => {
                  if (_isDate(date)) {
                    return DateTime.fromJSDate(date, {
                      zone: 'utc',
                    })
                      .toLocal()
                      .toFormat('H:mm ZZZZ - d LLL yyyy');
                  }
                  return DateTime.fromISO(date, {
                    zone: 'utc',
                  })
                    .toLocal()
                    .toFormat('H:mm ZZZZ - d LLL yyyy');
                },
              },
              language: 'en',
              commentsOnly: true,
              collaboration: { channelId },
              comments: {
                editorConfig: {
                  extraPlugins: [MentionPlugin, CustomMentionView],
                  mention: {
                    feeds: [
                      {
                        marker: '@',
                        feed: (queryText) =>
                          getFeedItems(
                            queryText,
                            usersForCommenting,
                            projectId,
                            isEntitySharedWithClient
                          ),
                        itemRenderer: customItemRenderer,
                        minimumCharacters: 1,
                      },
                    ],
                  },
                },
                CommentThreadView: CustomCommentThreadView(
                  dispatch,
                  commentThreads,
                  isEntitySharedWithClient,
                  currentUser,
                  usersForCommenting,
                  entityVersion,
                  isProjectDisabled,
                  isFakeClient
                ),
                CommentView: CustomCommentView(
                  commentThreads,
                  usersForCommenting,
                  isProjectDisabled,
                  projectId,
                  currentUser
                ),
              },
            }}
            data={children}
          />
        )}
    </div>
  );
};

export default CommentEditor;
